import { useMemo } from "react";
import { AuthUserType } from "../auth/types";
import { Role } from "../auth/types/roles";

type PermissionFlags = {
  canViewPatients: boolean;
  canCreatePatient: boolean;
  canEditPatient: boolean;
  canDeletePatient: boolean;
  canExportPatients: boolean;
};

/**
 * Centralized patient permissions mapping based on `user.roleId`.
 * Mirrors backend RolesEnum and route guards for consistency.
 */
export const usePatientPermissions = (user?: AuthUserType) => {
  return useMemo<PermissionFlags>(() => {
    const role = user?.roleId;

    const isAdmin = role === Role.ADMIN;
    const isReception = role === Role.ASSISTANT || role === Role.RECEPTIONIST;
    const isClinician = role === Role.PROFESSIONAL || role === Role.CLINICIAN;
    const isManager = role === Role.MANAGER || role === Role.ACCOUNTANT;
    const isAccountant = role === Role.ACCOUNTANT;
    const isPatient = role === Role.PATIENT;

    if (isAdmin) {
      return {
        canViewPatients: true,
        canCreatePatient: true,
        canEditPatient: true,
        canDeletePatient: true,
        canExportPatients: true,
      };
    }

    // Role-based permissions
    const canViewPatients = Boolean(role) && !isPatient;
    const canCreatePatient = isReception || isClinician || isManager;
    const canEditPatient = isManager;
    const canDeletePatient = isManager || isReception;
    const canExportPatients = isManager || isAccountant;

    return {
      canViewPatients,
      canCreatePatient,
      canEditPatient,
      canDeletePatient,
      canExportPatients,
    };
  }, [user?.roleId]);
};

export type { PermissionFlags };
